import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaFire } from "react-icons/fa";
import backendApi from "../api/backendApi";
import CategoryBreakdown from "./dashboard/CategoryBreakdown";
import DashboardSummaryCards from "./dashboard/DashboardSummaryCards";
import MonthlyTrendChart from "./dashboard/MonthlyTrendChart";
import RecentPayments from "./dashboard/RecentPayments";
import RegularityTimeline from "./dashboard/RegularityTimeline";
import {
  buildYearOptions,
  getKualaLumpurYear,
  overallStatusLabel,
} from "./dashboard/dashboardUtils";

function getErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || error.message || "Failed to load dashboard";
}

const Dashboard = () => {
  const [selectedYear, setSelectedYear] = useState(getKualaLumpurYear);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const yearOptions = useMemo(() => buildYearOptions(), []);
  const regularity = dashboard?.regularity || {};
  const currentStreak = Number(regularity.currentStreak || 0);
  const currentMonthStatus = regularity.currentMonthStatus || regularity.status;

  useEffect(() => {
    let ignore = false;

    async function loadDashboard() {
      setLoading(true);
      setError(null);

      try {
        const res = await backendApi.get("/dashboard/me", {
          params: { year: selectedYear },
        });
        if (!ignore) setDashboard(res.data);
      } catch (e) {
        if (!ignore) {
          setDashboard(null);
          setError(getErrorMessage(e));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadDashboard();

    return () => {
      ignore = true;
    };
  }, [selectedYear]);

  return (
    <div className="min-h-full bg-[#f6f3ea] p-4 md:rounded-3xl md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <header className="overflow-hidden rounded-[2rem] bg-[#063f2f] text-white shadow">
          <div className="relative grid gap-6 p-5 md:p-7 lg:grid-cols-[1fr_320px]">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-amber-300/20 blur-2xl" />
            <div className="absolute bottom-0 left-10 h-24 w-24 rounded-full bg-emerald-300/10 blur-xl" />

            <div className="relative">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-emerald-100">
                Your Chanda Dashboard
              </p>
              <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
                <div>
                  <p className="text-sm font-semibold text-emerald-100">
                    Current streak
                  </p>
                  <h1 className="mt-1 flex flex-wrap items-center gap-1 text-5xl font-black tracking-tight md:text-6xl">
                    <span className="pb-1 text-orange-400">{currentStreak}</span>
                    {currentStreak > 0 && (
                      <FaFire className="text-4xl text-orange-400 drop-shadow-[0_0_12px_rgba(251,146,60,0.65)] md:text-5xl" />
                    )}
                    <span className="text-2xl font-bold text-emerald-100">
                      month{currentStreak === 1 ? "" : "s"}
                    </span>
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-emerald-50">
                    {regularity.nextActionMessage ||
                      "Track your Mandatory Chanda streak and successful payments by reporting month."}
                  </p>
                </div>
              </div>

              {dashboard?.user && (
                <p className="mt-5 text-sm text-emerald-100">
                  {dashboard.user.name || "Member"} | {dashboard.user.tanzeem} |{" "}
                  {dashboard.user.muqami || "Muqami not set"}
                </p>
              )}

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/pay"
                  className="rounded-2xl bg-lime-300 px-5 py-3 text-center text-sm font-black text-emerald-950 shadow transition hover:-translate-y-0.5 hover:bg-lime-200"
                >
                  Pay Chanda
                </Link>
                {currentMonthStatus && (
                  <span className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-center text-sm font-semibold text-emerald-50">
                    {overallStatusLabel(currentMonthStatus)}
                  </span>
                )}
              </div>
            </div>

            <div className="relative rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur">
              <label className="text-sm font-medium text-emerald-50">
                Reporting Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="mt-2 w-full rounded-xl border border-white/20 bg-white px-3 py-2 text-sm font-semibold text-emerald-950 outline-none"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </header>

        {loading && (
          <div className="rounded-2xl bg-white p-6 text-sm text-zinc-500 shadow">
            Loading your dashboard...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700 shadow">
            {error}
          </div>
        )}

        {!loading && !error && dashboard && (
          <>
            <DashboardSummaryCards data={dashboard} />
            <RegularityTimeline regularity={dashboard.regularity} />
            <MonthlyTrendChart
              trends={dashboard.monthlyTrends}
              categoryTotals={dashboard.categoryTotals}
            />
            <CategoryBreakdown items={dashboard.categoryTotals} />
            <RecentPayments payments={dashboard.recentPayments} />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
